import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, signal } from '@angular/core';
import { Theme } from '../models';
import { ProfileService } from '../api/profile-account.service';
import { lastValueFrom } from 'rxjs';

/**
 * Manage Light/Dark mode theme
 */
@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  static DEFAULT_LIGHT_THEME: Theme = Theme.Light;
  static DEFAULT_DARK_THEME: Theme = Theme.Dark;

  // Signal to track current theme
  currentTheme = signal<Theme>(Theme.System);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private profileService: ProfileService,
  ) {}

  /**
   * Initialize theme from profile data or fallback to system preference
   */
  initializeTheme() {
    const profile = this.profileService.getProfile();
    if (profile?.settings?.theme) {
      this.setThemeFromProfile(profile.settings.theme);
    } else {
      this.setThemeFromSystemPreference();
    }
  }

  /**
   * Set theme from profile settings
   */
  setThemeFromProfile(profileTheme: 'system' | 'light' | 'dark') {
    const theme = this.mapProfileThemeToTheme(profileTheme);
    this.currentTheme.set(theme);
    this.applyTheme(theme);
  }

  /**
   * Set theme from system preference
   */
  setThemeFromSystemPreference() {
    const theme = this.getIsUserOsInDarkMode() ? Theme.Dark : Theme.Light;
    this.currentTheme.set(theme);
    this.applyTheme(theme);
  }

  /**
   * Returns current theme
   */
  getTheme(): Theme {
    return this.currentTheme();
  }

  /**
   * Toggle between light and dark theme and updates profile
   */
  async toggleLightAndDarkTheme() {
    const currentTheme = this.getTheme();
    const newTheme = currentTheme === Theme.Light ? Theme.Dark : Theme.Light;

    this.currentTheme.set(newTheme);
    this.applyTheme(newTheme);

    // Update profile with new theme
    await this.updateProfileTheme(newTheme);
  }

  /**
   * Update theme in profile settings
   */
  private async updateProfileTheme(theme: Theme) {
    const profile = this.profileService.getProfile();
    if (profile?.id) {
      const profileTheme = this.mapThemeToProfileTheme(theme);
      const updatedProfile = await lastValueFrom(
        this.profileService.updateProfileSettings(profile.id, {
          theme: profileTheme,
        }),
      );

      if (updatedProfile) {
        this.profileService.setProfile(updatedProfile);
      }
    }
  }

  /**
   * Map profile theme to Theme enum
   */
  private mapProfileThemeToTheme(
    profileTheme: 'system' | 'light' | 'dark',
  ): Theme {
    switch (profileTheme) {
      case 'light':
        return Theme.Light;
      case 'dark':
        return Theme.Dark;
      case 'system':
      default:
        return this.getIsUserOsInDarkMode() ? Theme.Dark : Theme.Light;
    }
  }

  /**
   * Map Theme enum to profile theme
   */
  private mapThemeToProfileTheme(theme: Theme): 'system' | 'light' | 'dark' {
    switch (theme) {
      case Theme.Light:
        return 'light';
      case Theme.Dark:
        return 'dark';
      case Theme.System:
      default:
        return 'system';
    }
  }

  /**
   * Check if user OS in dark mode
   */
  private getIsUserOsInDarkMode(): boolean {
    return (
      this.document.defaultView?.window?.matchMedia(
        '(prefers-color-scheme: dark)',
      ).matches || false
    );
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: Theme) {
    const themeLink = this.document.querySelector(
      '#theme-css',
    ) as HTMLLinkElement;

    if (themeLink) {
      // For system theme, use the resolved theme (light or dark)
      const resolvedTheme =
        theme === Theme.System
          ? this.getIsUserOsInDarkMode()
            ? Theme.Dark
            : Theme.Light
          : theme;
      themeLink.href = `${resolvedTheme}.css`;
    }
  }
}
